<script setup>
import { usePaymentsStore } from '@/store/modules/payments';

const paymentsStore = usePaymentsStore();

const props = defineProps({
  ticketCountData: {
    required: true,
  },
  harmonyLogData: {
    required: true,
  },
  hasPermission: {
    required: true,
  },
  loadingTicket: {
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
});

/** 인원수별 분석권 — 총합 */
const ticketTotal = computed(() => {
  const details = paymentsStore.harmonyTicketDetails;
  return (details.members2.count || 0) + (details.members3.count || 0) + (details.members4.count || 0);
});

/** 인원수별 분석권 — 총 N건 */
const ticketTotalText = computed(() => `총 ${ticketTotal.value}건`);

/** 분석 로그가 없어도 보유한 분석권이 있으면 건수 뱃지를 표시 */
const showTicketBadge = computed(() => props.hasPermission || ticketTotal.value > 0);

/** 인원수별 분석권 — 2인 1건 · 3인 1건 */
const ticketDetailText = computed(() => {
  const details = paymentsStore.harmonyTicketDetails;
  const parts = [];
  if (details.members2.count > 0) parts.push(`2인 ${details.members2.count}건`);
  if (details.members3.count > 0) parts.push(`3인 ${details.members3.count}건`);
  if (details.members4.count > 0) parts.push(`4인 ${details.members4.count}건`);
  return parts.join(' · ');
});
</script>

<template>
  <div class="si-overview__cover si-overview__cover--harmony" :class="{ mobile }">
    <div class="si-overview__cover-inner">
      <div class="harmony-cover__header">
        <div class="harmony-cover__title">
          <span class="harmony-cover__subtitle">인연의 흐름을 읽다</span>
          <span class="harmony-cover__service">낭만궁합</span>
        </div>
        <div class="harmony-cover__badge">
          <template v-if="!loadingTicket && showTicketBadge">
            <span class="harmony-cover__badge-tag">궁합분석권</span>
            <span class="harmony-cover__badge-date">
              <span class="harmony-cover__badge-total">{{ ticketTotalText }}</span>
              <template v-if="ticketDetailText">
                <span class="harmony-cover__badge-separator">/</span>
                <span class="harmony-cover__badge-detail">{{ ticketDetailText }}</span>
              </template>
            </span>
          </template>
          <template v-if="!loadingTicket && !showTicketBadge">
            <span class="harmony-cover__badge-tag">구매 후 이용가능</span>
          </template>
        </div>
      </div>

      <div class="harmony-cover__img">
        <img src="@/assets/images_manse/nangman-harmony/img_cover_harmony.png" alt="낭만궁합" />
      </div>

      <div v-if="mobile" class="harmony-cover__info-box">
        <div class="harmony-cover__info-title">서로의 다름을 이해하는 가장 부드러운 방법</div>
        <div class="harmony-cover__info-text">
          가까워질수록 기대와 서운함도 함께 커집니다.<br />
          이제 막 알아가는 관계에도 이해가 필요하죠!<br />
          서로의 스타일과 에너지를 사주 오행의 균형 속에서<br />
          새롭게 조명해 볼 수 있어요.
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_analysis_main_color.scss';

.harmony-cover__header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.harmony-cover__title {
  display: flex;
  flex-direction: column;
  color: #333;
  font-family: Pretendard;
}

.harmony-cover__subtitle {
  font-weight: 400;
  font-size: 32px;
  line-height: 44px;
}

.harmony-cover__service {
  font-size: 32px;
  font-weight: 700;
  line-height: 44px;
}

.harmony-cover__badge {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 26px;
  width: 100%;
}

.harmony-cover__badge-tag {
  display: inline-flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 18px;
  background: $analysis-harmony;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.harmony-cover__badge-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Pretendard;
  font-size: 14px;
  line-height: 18px;
}

.harmony-cover__badge-total {
  color: #eb4c10;
  font-weight: 700;
}

.harmony-cover__badge-separator {
  color: #999;
  font-weight: 500;
}

.harmony-cover__badge-detail {
  color: #333;
  font-weight: 500;
}

.harmony-cover__img {
  margin-top: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  img {
    max-width: 500px;
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}

.si-overview__cover--harmony.mobile {
  height: auto;
  max-height: none;
  min-height: calc(100dvh - 52px);
  padding: 30px 16px 160px;

  .harmony-cover__img {
    margin-top: 20px;

    img {
      max-width: 500px;
      width: 100%;
    }
  }

  .harmony-cover__info-box {
    margin-top: 20px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .harmony-cover__info-title {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 26px;
    padding-left: 12px;
  }

  .harmony-cover__info-text {
    background: var(--gray-100-white, #fff);
    border-radius: 12px;
    padding: 16px;
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
